import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Lock,
  Pause,
  Play,
  ShieldAlert,
  LogOut,
  CheckCircle2,
  XCircle,
  Loader2,
  FileSpreadsheet,
  History,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/apagon`;
const SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1SfoHdL1Pc8XL9SWv5_GhoFNpRR8fUl-T1JKC2vubp1Y/edit?gid=0#gid=0";

const ControlPanelPage = () => {
  const [pin, setPin] = useState("");
  const [unlockedPin, setUnlockedPin] = useState(null); // store the validated pin in memory
  const [currentUser, setCurrentUser] = useState(null);  // resolved user name from PIN
  const [pinError, setPinError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const [confirmAction, setConfirmAction] = useState(null); // {action, market, label}
  const [status, setStatus] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // ─── Available pause markets (besides "all" which is the big red button)
  const PAUSE_MARKETS = [
    { market: "fr_nrj", label: "FR · NRJ",  flag: "🇫🇷" },
    { market: "fr_box", label: "FR · BOX",  flag: "🇫🇷" },
    { market: "es_nrj", label: "ES · NRJ",  flag: "🇪🇸" },
    { market: "es_box", label: "ES · BOX",  flag: "🇪🇸" },
    { market: "it_nrj", label: "IT · NRJ",  flag: "🇮🇹" },
  ];

  const MARKET_LABEL = {
    all: "Tous marchés",
    fr_nrj: "FR · NRJ", fr_box: "FR · BOX",
    es_nrj: "ES · NRJ", es_box: "ES · BOX",
    it_nrj: "IT · NRJ",
  };

  // Inject noindex meta + no-referrer
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Apagon";

    // Override existing static robots meta (present in index.html)
    let robotsMeta = document.querySelector('meta[name="robots"]');
    const previousRobots = robotsMeta ? robotsMeta.getAttribute("content") : null;
    const robotsCreated = !robotsMeta;
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", "noindex, nofollow, noarchive, nosnippet");

    const referrerMeta = document.createElement("meta");
    referrerMeta.name = "referrer";
    referrerMeta.content = "no-referrer";
    document.head.appendChild(referrerMeta);

    return () => {
      document.title = prevTitle;
      if (robotsCreated) {
        robotsMeta.remove();
      } else if (previousRobots !== null) {
        robotsMeta.setAttribute("content", previousRobots);
      }
      document.head.removeChild(referrerMeta);
    };
  }, []);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (!pin) return;
    setVerifying(true);
    setPinError("");
    try {
      const res = await axios.post(`${API_BASE}/verify`, { pin });
      if (res.data?.valid) {
        setUnlockedPin(pin);
        setCurrentUser(res.data.user || null);
        setPin("");
      } else {
        setPinError("PIN incorrect");
        setAttempts((a) => a + 1);
        setPin("");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 401) {
        setPinError("PIN incorrect");
      } else {
        setPinError(detail || "Erreur de vérification");
      }
      setAttempts((a) => a + 1);
      setPin("");
    } finally {
      setVerifying(false);
    }
  };

  const handleLogout = () => {
    setUnlockedPin(null); setCurrentUser(null);
    setPin("");
    setStatus(null);
  };

  const fetchLog = React.useCallback(async (pinValue) => {
    const usePin = pinValue ?? unlockedPin;
    if (!usePin) return;
    setLogLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/log`, {
        params: { pin: usePin, limit: 50 },
      });
      setLogEntries(res.data?.entries || []);
    } catch (err) {
      // Silent — log is best-effort, panel still functional without
      console.warn("Failed to load apagon log", err);
    } finally {
      setLogLoading(false);
    }
  }, [unlockedPin]);

  // Auto-fetch log when unlocked
  useEffect(() => {
    if (unlockedPin) fetchLog(unlockedPin);
  }, [unlockedPin, fetchLog]);

  const triggerWebhook = async ({ action, market = "all" }) => {
    setStatus({ type: "loading", action, market, message: "Déclenchement en cours..." });
    try {
      const res = await axios.post(`${API_BASE}/trigger`, {
        pin: unlockedPin,
        action,
        market,
      });
      setStatus({
        type: "success",
        action,
        market,
        message:
          (action === "pause"
            ? `Webhook 'Pause ${market === "all" ? "all campaigns" : market.toUpperCase()}' déclenché.`
            : "Webhook 'Enable all campaigns' déclenché.") +
          ` (HTTP ${res.data?.webhook_status ?? "OK"})`,
      });
      fetchLog();  // refresh history after each trigger
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      // Session expired (PIN no longer valid) → re-lock
      if (err?.response?.status === 401) {
        setUnlockedPin(null); setCurrentUser(null);
        setPinError("Session expirée, ressaisissez le PIN.");
        return;
      }
      setStatus({
        type: "error",
        action,
        market,
        message: `Échec : ${detail}`,
      });
      fetchLog();  // refresh — failed attempts are also logged
    }
  };

  const handleConfirm = () => {
    const c = confirmAction;
    setConfirmAction(null);
    if (c) triggerWebhook(c);
  };

  // ─── Derived: warn if user clicks "enable" within 5 min of the last successful pause.
  // The pause rules at Make take a few minutes to propagate to Google Ads — re-enabling
  // immediately could collide with the in-flight pause and leave campaigns in a weird state.
  const PAUSE_PROPAGATION_WINDOW_MS = 5 * 60 * 1000;
  const recentPauseWarning = React.useMemo(() => {
    if (!confirmAction || confirmAction.action !== "enable") return null;
    const lastPause = logEntries.find((e) => e.action === "pause" && e.success);
    if (!lastPause?.timestamp) return null;
    const ageMs = Date.now() - new Date(lastPause.timestamp).getTime();
    if (ageMs < 0 || ageMs >= PAUSE_PROPAGATION_WINDOW_MS) return null;
    const minutesAgo = Math.floor(ageMs / 60000);
    const secondsAgo = Math.floor((ageMs % 60000) / 1000);
    const ageStr =
      minutesAgo > 0
        ? `il y a ${minutesAgo} min ${secondsAgo}s`
        : `il y a ${secondsAgo} secondes`;
    return {
      ageStr,
      market: MARKET_LABEL[lastPause.market] || lastPause.market,
    };
  }, [confirmAction, logEntries, MARKET_LABEL]);

  // ============== PIN GATE ==============
  if (!unlockedPin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4">
        <div className="w-full max-w-md bg-slate-800/60 backdrop-blur border border-slate-700 rounded-2xl p-8 shadow-2xl">
          <div className="flex flex-col items-center mb-6">
            <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-amber-400" />
            </div>
            <h1
              className="text-2xl font-bold text-white"
              data-testid="apagon-pin-title"
            >
              Restricted Access
            </h1>
            <p className="text-slate-400 text-sm mt-2 text-center">
              Veuillez saisir le code PIN pour continuer
            </p>
          </div>

          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div>
              <Label htmlFor="pin-input" className="text-slate-300">
                Code PIN
              </Label>
              <Input
                id="pin-input"
                data-testid="pin-input"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                className="mt-2 bg-slate-900 border-slate-700 text-white text-center text-2xl tracking-[0.5em] h-14"
                autoFocus
                disabled={verifying}
              />
              {pinError && (
                <p className="text-red-400 text-sm mt-2" data-testid="pin-error">
                  {pinError} {attempts > 2 && `(${attempts} tentatives)`}
                </p>
              )}
            </div>
            <Button
              type="submit"
              data-testid="pin-submit-button"
              disabled={verifying || !pin}
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold h-12"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Vérification...
                </>
              ) : (
                "Déverrouiller"
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ============== CONTROL PANEL ==============
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-10">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-7 h-7 text-amber-400" />
            <div>
              <h1 className="text-3xl font-bold text-white" data-testid="panel-heading">
                Apagon — Control Panel
              </h1>
              {currentUser && (
                <p className="text-sm text-slate-400 mt-0.5" data-testid="current-user">
                  Connecté en tant que <span className="font-semibold text-amber-300">{currentUser}</span>
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" /> Verrouiller
          </Button>
        </div>

        {/* Big actions: pause all + enable all */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-slate-800/60 backdrop-blur border border-slate-700 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Pause className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-white font-semibold text-lg">Pause all campaigns</h3>
            </div>
            <p className="text-slate-400 text-sm mb-5">
              Déclenche le webhook qui met en pause <strong>toutes les campagnes, tous marchés
              confondus</strong>. Une confirmation sera demandée.
            </p>
            <Button
              data-testid="pause-button"
              onClick={() => setConfirmAction({ action: "pause", market: "all", label: "all campaigns" })}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold h-12"
            >
              <Pause className="w-4 h-4 mr-2" /> Pause all campaigns
            </Button>
          </div>

          <div className="bg-slate-800/60 backdrop-blur border border-slate-700 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Play className="w-5 h-5 text-green-400" />
              </div>
              <h3 className="text-white font-semibold text-lg">Enable all campaigns</h3>
            </div>
            <p className="text-slate-400 text-sm mb-5">
              Déclenche le webhook qui réactive toutes les campagnes (peu importe ce qui était
              en pause). Une confirmation sera demandée.
            </p>
            <Button
              data-testid="enable-button"
              onClick={() => setConfirmAction({ action: "enable", market: "all", label: "all campaigns" })}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold h-12"
            >
              <Play className="w-4 h-4 mr-2" /> Enable all campaigns
            </Button>
          </div>
        </div>

        {/* Per-market pause section */}
        <div className="mt-6 bg-slate-800/60 backdrop-blur border border-slate-700 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Pause className="w-4 h-4 text-amber-400" />
            </div>
            <h3 className="text-white font-semibold text-lg">Pause ciblée par marché</h3>
          </div>
          <p className="text-slate-400 text-sm mb-4">
            Met en pause un seul marché. Pour réactiver, utilise le bouton{" "}
            <strong className="text-green-300">Enable all campaigns</strong> ci-dessus
            (un seul webhook de réactivation, quel que soit le marché paussé).
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {PAUSE_MARKETS.map((m) => (
              <Button
                key={m.market}
                data-testid={`pause-${m.market}-button`}
                onClick={() =>
                  setConfirmAction({ action: "pause", market: m.market, label: m.label })
                }
                className="h-12 bg-slate-900/70 hover:bg-red-600/90 border border-slate-700 hover:border-red-500 text-slate-200 hover:text-white font-semibold transition-colors"
              >
                <span className="mr-2 text-base leading-none">{m.flag}</span>
                <Pause className="w-3.5 h-3.5 mr-1.5 opacity-70" />
                {m.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Status feedback */}
        {status && (
          <div
            data-testid="status-feedback"
            className={`mt-6 p-4 rounded-xl border flex items-start gap-3 ${
              status.type === "success"
                ? "bg-green-500/10 border-green-500/40 text-green-300"
                : status.type === "error"
                ? "bg-red-500/10 border-red-500/40 text-red-300"
                : "bg-slate-700/40 border-slate-600 text-slate-200"
            }`}
          >
            {status.type === "loading" && (
              <Loader2 className="w-5 h-5 animate-spin shrink-0" />
            )}
            {status.type === "success" && <CheckCircle2 className="w-5 h-5 shrink-0" />}
            {status.type === "error" && <XCircle className="w-5 h-5 shrink-0" />}
            <div className="text-sm">
              <div className="font-semibold">
                {status.action === "pause"
                  ? `Pause ${status.market === "all" ? "all campaigns" : status.market.toUpperCase()}`
                  : "Enable all campaigns"}
              </div>
              <div className="opacity-90">{status.message}</div>
            </div>
          </div>
        )}

        {/* History / Audit log */}
        <div className="mt-6 bg-slate-800/60 backdrop-blur border border-slate-700 rounded-2xl p-6" data-testid="apagon-log">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-500/20 flex items-center justify-center">
                <History className="w-4 h-4 text-blue-400" />
              </div>
              <h3 className="text-white font-semibold text-lg">Historique des déclenchements</h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchLog()}
              disabled={logLoading}
              data-testid="apagon-log-refresh"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${logLoading ? "animate-spin" : ""}`} />
              Recharger
            </Button>
          </div>
          {logEntries.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">
              {logLoading ? "Chargement..." : "Aucun déclenchement enregistré pour l'instant."}
            </p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-700/60">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <div className="col-span-3 sm:col-span-3">Date</div>
                <div className="col-span-3 sm:col-span-2">Par qui</div>
                <div className="col-span-2 sm:col-span-2">Action</div>
                <div className="col-span-2 sm:col-span-2">Marché</div>
                <div className="col-span-2 sm:col-span-1 text-right">Statut</div>
                <div className="hidden sm:block sm:col-span-2 truncate">IP</div>
              </div>
              <div className="divide-y divide-slate-700/60 max-h-[400px] overflow-y-auto">
                {logEntries.map((e) => {
                  const dt = e.timestamp ? new Date(e.timestamp) : null;
                  const dateStr = dt
                    ? dt.toLocaleString("fr-FR", {
                        day: "2-digit", month: "2-digit", year: "2-digit",
                        hour: "2-digit", minute: "2-digit",
                      })
                    : "—";
                  const isPause = e.action === "pause";
                  const ok = e.success;
                  const userName = e.user || "—";
                  const userColor =
                    userName === "Ulysse"  ? "text-amber-300 bg-amber-500/10" :
                    userName === "Vincent" ? "text-cyan-300 bg-cyan-500/10" :
                    userName === "Auriane" ? "text-fuchsia-300 bg-fuchsia-500/10" :
                    "text-slate-400 bg-slate-500/10";
                  return (
                    <div
                      key={e.id || e.timestamp}
                      className="grid grid-cols-12 gap-2 px-3 py-2.5 text-xs items-center hover:bg-slate-900/40 transition-colors"
                      data-testid="apagon-log-entry"
                    >
                      <div className="col-span-3 sm:col-span-3 text-slate-300 font-mono tabular-nums">{dateStr}</div>
                      <div className="col-span-3 sm:col-span-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${userColor}`}>
                          {userName}
                        </span>
                      </div>
                      <div className="col-span-2 sm:col-span-2">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            isPause ? "bg-red-500/15 text-red-300" : "bg-green-500/15 text-green-300"
                          }`}
                        >
                          {isPause ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                          {isPause ? "Pause" : "Enable"}
                        </span>
                      </div>
                      <div className="col-span-2 sm:col-span-2 text-slate-300 font-medium truncate">
                        {MARKET_LABEL[e.market] || e.market}
                      </div>
                      <div className="col-span-2 sm:col-span-1 text-right">
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-bold tabular-nums ${
                            ok ? "text-emerald-400" : "text-red-400"
                          }`}
                        >
                          {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {e.webhook_status ?? "ERR"}
                        </span>
                      </div>
                      <div className="hidden sm:block sm:col-span-2 text-slate-500 font-mono truncate text-[10px]" title={e.ip}>
                        {e.ip || "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-3">
            Audit RGPD — chaque clic est journalisé (date, action, marché, IP, statut).
          </p>
        </div>

        {/* Google Sheet reference */}
        <a
          href={SHEET_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sheet-link"
          className="mt-6 flex items-center justify-between gap-3 p-4 rounded-xl border border-slate-700 bg-slate-800/60 backdrop-blur hover:bg-slate-700/60 hover:border-slate-600 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <div className="text-white font-semibold">Google Sheet de référence</div>
              <div className="text-slate-400 text-xs">
                Ouvre le document dans un nouvel onglet
              </div>
            </div>
          </div>
          <span className="text-emerald-400 text-sm font-medium">Ouvrir →</span>
        </a>

        <p className="text-xs text-slate-500 mt-8 text-center">
          Les URLs des webhooks sont stockées côté serveur. Le PIN est validé côté serveur à
          chaque action.
        </p>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog
        open={!!confirmAction}
        onOpenChange={(o) => !o && setConfirmAction(null)}
      >
        <AlertDialogContent
          data-testid="confirm-dialog"
          className="bg-slate-800 border-slate-700 text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {confirmAction?.action === "pause"
                ? `Confirmer : Pause ${confirmAction.label} ?`
                : "Confirmer : Enable all campaigns ?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {confirmAction?.action === "pause"
                ? confirmAction.market === "all"
                  ? "Cette action va déclencher immédiatement le webhook qui met TOUTES les campagnes (tous marchés) en pause."
                  : `Cette action va déclencher immédiatement le webhook qui met en pause UNIQUEMENT le marché ${confirmAction.label}. Les autres marchés ne seront pas touchés.`
                : "Cette action va déclencher immédiatement le webhook qui réactive TOUTES les campagnes (peu importe ce qui était en pause)."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {recentPauseWarning && (
            <div
              data-testid="recent-pause-warning"
              className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-200 text-sm"
            >
              <AlertTriangle className="w-5 h-5 shrink-0 text-amber-400 mt-0.5" />
              <div>
                <div className="font-semibold mb-1">
                  Attention — pause récente détectée
                </div>
                <p className="text-amber-200/90 leading-relaxed">
                  Un webhook <strong>Pause ({recentPauseWarning.market})</strong> a été
                  déclenché <strong>{recentPauseWarning.ageStr}</strong>. La règle de pause
                  côté Make/Google Ads peut prendre <strong>~5 minutes</strong> à se propager
                  totalement.
                </p>
                <p className="text-amber-200/90 leading-relaxed mt-1.5">
                  Réactiver maintenant peut entrer en collision avec la pause en cours et
                  laisser certaines campagnes dans un état incohérent. Il est recommandé{" "}
                  <strong>d'attendre que la pause soit complètement appliquée</strong> avant
                  de réactiver.
                </p>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="confirm-cancel-button"
              className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-trigger-button"
              onClick={handleConfirm}
              className={
                confirmAction?.action === "pause"
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-green-600 hover:bg-green-700 text-white"
              }
            >
              {confirmAction?.action === "pause"
                ? `Oui, mettre en pause${confirmAction.market === "all" ? "" : ` (${confirmAction.label})`}`
                : "Oui, activer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ControlPanelPage;
